import { hasStoredAnalyticsConsent } from 'utils/cookieConsent';
import { isGtmEnabled } from 'utils/tracking';

type QueuedEvent = { name: string; params?: Record<string, unknown> };

let analyticsReady = false;
const readyListeners = new Set<() => void>();
const eventQueue: QueuedEvent[] = [];

export function ensureGtagStub(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }
}

export function pushConsentUpdate(opts: {
  analytics: boolean;
  marketing: boolean;
}): void {
  if (!isGtmEnabled()) return;
  ensureGtagStub();
  window.gtag!('consent', 'update', {
    analytics_storage: opts.analytics ? 'granted' : 'denied',
    ad_storage: opts.marketing ? 'granted' : 'denied',
    ad_user_data: opts.marketing ? 'granted' : 'denied',
    ad_personalization: opts.marketing ? 'granted' : 'denied',
  });
}

export function setAnalyticsReady(ready: boolean): void {
  analyticsReady = ready;
  if (!ready) return;
  readyListeners.forEach((listener) => listener());
  readyListeners.clear();
  flushQueuedEvents();
}

export function isAnalyticsReady(): boolean {
  return analyticsReady;
}

export function whenAnalyticsReady(timeoutMs = 8000): Promise<void> {
  if (analyticsReady) return Promise.resolve();
  ensureGtagStub();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      readyListeners.delete(done);
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    readyListeners.add(done);
  });
}

function flushQueuedEvents(): void {
  if (!window.gtag) return;
  while (eventQueue.length > 0) {
    const { name, params } = eventQueue.shift()!;
    window.gtag('event', name, params);
  }
}

export function queueOrRunGtagEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!isGtmEnabled() || !hasStoredAnalyticsConsent()) return;
  ensureGtagStub();
  if (analyticsReady && window.gtag) {
    window.gtag('event', eventName, params);
    return;
  }
  eventQueue.push({ name: eventName, params });
}

/** Call once at app startup so early ecommerce events are not dropped. */
export function initGtagIfConsented(): void {
  if (isGtmEnabled() && hasStoredAnalyticsConsent()) {
    ensureGtagStub();
    setAnalyticsReady(true);
  }
}
